import * as Sentry from '@sentry/browser';
import { useAppConfig } from '@/appConfig/useAppConfig.ts';
import { useRef, useState } from 'react';
import { useTransmitRequest } from '@/saveRegistration/actions/useTransmitRequest.ts';
import { getCsrfToken } from '@/httpClient/requests/getCsrfToken.ts';

interface UseTransmitRegistrationApi {
  transmitRegistrationStatus: TransmitRegistrationStatus;
  transmit: () => Promise<void>;
}

export type TransmitRegistrationStatus =
  | 'unknown'
  | 'transmitting'
  | 'transmitted-success'
  | 'transmitted-failure'
  | 'transmitted-unknown-failure';

export const useTransmitRegistration = ({ spinnerDuration = 1000 }): UseTransmitRegistrationApi => {
  const meta = useAppConfig();
  const [transmitRegistrationStatus, setTransmitRegistrationStatus] = useState<TransmitRegistrationStatus>('unknown');

  const retry403ref = useRef<boolean>(false);

  const { transmitExistingFormPayload } = useTransmitRequest();

  const transmit = () => {
    setTransmitRegistrationStatus('transmitting');
    if (!meta) {
      // Checking for `meta` existence so the 'transmitRequest' does not throw
      setTimeout(() => {
        setTransmitRegistrationStatus('transmitted-unknown-failure');
      }, spinnerDuration);
      console.error('Metadata Error: AppConfig is empty');
      Sentry.captureException({ message: "Metadata Error: No 'registrations' url found (in http-meta tags)" });
      return Promise.reject();
    }

    return transmitExistingFormPayload()
      .then((res) => {
        if (res.status === 202) {
          // HttpStatus.ACCEPTED
          // mark as transmitted
          setTransmitRegistrationStatus('transmitted-success');
        } else {
          console.error('Transmitting registration to backend: Unknown error');
          setTimeout(() => {
            setTransmitRegistrationStatus('transmitted-unknown-failure');
          }, spinnerDuration);
        }
      })
      .catch((e) => {
        // Ignore error and move forward, when developing
        // if(isLocalDev()) {
        //   setTimeout(() => {
        //     setTransmitRegistrationStatus('transmitted-success');
        //   }, spinnerDuration)
        //   return;
        // }

        if (e?.response?.status === 403) {
          // This can happen if the CSRF Token is invalid/retired/missing
          // lets get one and retry automatically.

          if (retry403ref.current) {
            // Retrying did not work. Throw unknown error:
            console.error(e);
            setTimeout(() => {
              setTransmitRegistrationStatus('transmitted-unknown-failure');
            }, spinnerDuration);
            console.error('Transmitting registration to backend: Unknown error');
            Sentry.captureException(e);
          } else {
            // Generate a token, then try again.
            getCsrfToken().then(() => {
              retry403ref.current = true; // Only retry once.
              return transmit();
            });
          }
        } else if (e?.response?.status === 500) {
          setTimeout(() => {
            setTransmitRegistrationStatus('transmitted-unknown-failure');
          }, spinnerDuration);
          console.error('Transmitting registration to backend: Unknown error');
          Sentry.captureException(e);
        } else if (e?.response?.status === 400) {
          setTimeout(() => {
            setTransmitRegistrationStatus('transmitted-failure');
          }, spinnerDuration);
          console.error('Transmitting registration to backend: Could not pass on registration to DKMS ONE / IO');
          Sentry.captureException(e);
        } else {
          console.error(e);
          setTimeout(() => {
            setTransmitRegistrationStatus('transmitted-unknown-failure');
          }, spinnerDuration);
          console.error('Transmitting registration to backend: Unknown error');
          Sentry.captureException(e);
        }
      });
  };

  return { transmitRegistrationStatus, transmit };
};
